// ---------------------------------------------------------------------------
// Canonical domain types shared between the frontend (React) and backend (Hono).
// These mirror the Supabase schema in supabase/migrations and the PRD §8 model.
// ---------------------------------------------------------------------------

/** The six downstream artifacts every R&R job must end up with (PRD §6/§8). */
export type ArtifactType =
  | "sheet_row"
  | "agreement"
  | "invoice"
  | "removal_event"
  | "photo_album"
  | "reinstall_event";

/** Per-artifact lifecycle. `draft` = created but left for manual completion. */
export type ArtifactStatus = "pending" | "done" | "draft" | "failed";

/** Rollup status for the whole job. */
export type JobStatus =
  | "pending" // created, orchestration not started
  | "processing" // orchestration running
  | "complete" // all six artifacts done
  | "partial" // some done, some draft/failed
  | "failed"; // nothing succeeded / fatal

/** Canonical ordered list of orchestration steps (PRD §6). */
export const ARTIFACT_ORDER: ArtifactType[] = [
  "sheet_row",
  "agreement",
  "invoice",
  "removal_event",
  "photo_album",
  "reinstall_event",
];

export const ARTIFACT_LABELS: Record<ArtifactType, string> = {
  sheet_row: "Google Sheet row",
  agreement: "R&R agreement (DocuSign)",
  invoice: "Invoice",
  removal_event: "Removal calendar event",
  photo_album: "Removal photos (Drive folder)",
  reinstall_event: "Reinstall calendar event",
};

export interface JobArtifact {
  id: string;
  job_id: string;
  type: ArtifactType;
  external_id: string | null;
  url: string | null;
  status: ArtifactStatus;
  error: string | null;
  updated_at: string;
}

export interface Job {
  id: string;
  customer_name: string;
  address: string;
  phone: string;
  email: string;
  removal_at: string; // ISO timestamp
  reinstall_at: string | null; // ISO timestamp (derived)
  notes: string | null;
  invoice_amount: number | null;
  status: JobStatus;
  created_at: string;
}

export interface JobWithArtifacts extends Job {
  artifacts: JobArtifact[];
}

export interface JobRun {
  id: string;
  job_id: string;
  step: ArtifactType | "orchestrate";
  attempt: number;
  status: "ok" | "error";
  request: unknown;
  response: unknown;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Intake (PRD §7.1 / Appendix A)
// ---------------------------------------------------------------------------
export interface JobInput {
  customer_name: string;
  address: string;
  phone: string;
  email: string;
  removal_at: string; // ISO timestamp
  notes?: string;
  invoice_amount?: number | null;
  /** Optional client-generated key to make POST /api/jobs idempotent. */
  idempotency_key?: string;
}

// ---------------------------------------------------------------------------
// Settings / configuration (PRD §11 — everything editable, nothing hardcoded)
// ---------------------------------------------------------------------------
export interface SheetColumn {
  /** Column header as it appears in the existing sheet (for documentation). */
  header: string;
  /** Template string resolved per job, e.g. "{customer}" or "{removal_date}". */
  template: string;
}

export interface CalendarSettings {
  calendarId: string; // "primary" or a specific calendar id
  removalTitleFormat: string;
  reinstallTitleFormat: string;
  notesTemplate: string;
  defaultTime: string; // "HH:mm" used when intake omits a time
  durationMinutes: number;
  timezone: string; // IANA, e.g. "America/Chicago"
}

export interface ReinstallSettings {
  offsetDays: number; // default 7
  carryTime: boolean; // same time of day as removal
  carryAlbumLink: boolean; // include album link in reinstall notes too
}

export interface PhotosSettings {
  albumNameFormat: string;
}

export interface SheetSettings {
  spreadsheetId: string;
  tabName: string;
  columns: SheetColumn[];
}

export interface DocuSignSettings {
  templateId: string;
  signerRoleName: string;
  /** "sent" = send immediately; "draft" = create envelope for review (PRD §7.3). */
  sendMode: "sent" | "draft";
  /** Map of DocuSign tab/textfield label -> template string. */
  tabMap: Record<string, string>;
}

export interface InvoiceSettings {
  templateFileId: string;
  templateType: "doc" | "sheet";
  /** Map of placeholder token in the template -> template string. */
  placeholderMap: Record<string, string>;
  pricingDefault: number | null;
  deliverEmail: boolean;
}

export interface Settings {
  calendar: CalendarSettings;
  reinstall: ReinstallSettings;
  photos: PhotosSettings;
  sheet: SheetSettings;
  docusign: DocuSignSettings;
  invoice: InvoiceSettings;
}

// ---------------------------------------------------------------------------
// Connections (PRD §7.9)
// ---------------------------------------------------------------------------
export type Provider = "google" | "docusign";

export interface ConnectionStatus {
  provider: Provider;
  connected: boolean;
  expires_at: string | null;
  scopes: string[];
  healthy: boolean;
  detail?: string;
}

// ---------------------------------------------------------------------------
// API envelopes
// ---------------------------------------------------------------------------
export interface ApiError {
  error: string;
  details?: unknown;
}

export interface CurrentUser {
  email: string;
  authenticated: boolean;
}
